// ---------------------------------------------------------------------------
// The browser side of THE TRONC. V23c.
//
// `13-pay-and-the-upstream-data-contract.md` §4.6: a hospitality distribution is
// not pay, must not pass through the pay engine, and is "recorded as a separate
// distribution, referenced by the timesheet, never merged into gross".
//
// SEPARATE FROM `pay.ts` FOR THE SAME REASON THE TABLE IS SEPARATE FROM
// `app.pay_calculation`. If the two shared a module, the first person needing a
// total would sum them, and that sum is the merge §4.6 forbids.
//
// NO ARITHMETIC HERE OR IN THE SCREEN, and it is a stronger rule here than on
// the pay side: there is no engine to disagree with, because an amount is
// REPORTED by the troncmaster rather than computed by this product. Adding it to
// anything would be this product taking a position on somebody else's tax.
// `verify-V23c.sh` greps this file and the screen for the operators after
// stripping comments.
//
// AND NO VOCABULARY IS SPELLED HERE. A standing arrives as its id and the
// server's own label for it; a correction reason arrives as an id. Nothing in
// this directory decides what either means.
// ---------------------------------------------------------------------------

import { mutate, read } from "./console";

export type TroncScheme = {
  id: string;
  reference: string;
  /** The reference the tronc is run under, NOT a name. The database's CHECK
   *  forbids spaces so this column cannot become one — 0140 decision 2. */
  troncmaster_reference: string;
  /** Self-certified by the agency and verified by nothing. The screen says so
   *  beside it, because a field recording an unverified legal position that does
   *  not say it is unverified reads as an assurance. */
  is_independent_of_employer: boolean;
  currency: string;
  opened_on: string;
  closed_on: string | null;
  distribution_count: string;
};

export type TroncDistribution = {
  id: string;
  timesheet_id: string;
  scheme_id: string;
  scheme_reference: string;
  troncmaster_reference: string;
  is_independent_of_employer: boolean;
  /** The same pseudonym V22's Proof Pack uses. A tronc list is read beside a pay
   *  list and neither needs a name to be reconciled. */
  worker_ref: string;
  role_name: string;
  shift_start: string;
  period_start: string;
  period_end: string;
  amount_minor: string;
  currency: string;
  state: string;
  state_label: string;
  /** S70 (0203). The state row's tone; the screen compares no state id. */
  state_tone: string;
  supersedes_id: string | null;
  is_superseded: boolean;
  recorded_at: string;
};

/** GET /v1/my/tronc (0299): one of the caller's own current shares. No other person's. */
export type MyTroncShare = {
  distribution_id: string;
  event_name: string;
  period_start: string;
  period_end: string;
  amount_minor: string;
  currency: string;
  state: string;
  state_label: string;
  is_independent_of_employer: boolean;
  recorded_at: string;
};

export const tronc = {
  mine: (base: string) => read(base, "/v1/my/tronc"),
  schemes: (base: string) => read(base, "/v1/tronc/schemes"),
  /** S17. The standings a share can be recorded in, with the server's labels. */
  states: (base: string) => read(base, "/v1/reference/tronc-states"),
  recordScheme: (base: string, body: Record<string, unknown>) =>
    mutate("POST", base, "/v1/tronc/schemes", body),
  forEvent: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/tronc`),
  record: (base: string, timesheetId: string, body: Record<string, unknown>) =>
    mutate("POST", base, `/v1/timesheets/${encodeURIComponent(timesheetId)}/tronc`, body),
};
